import { z } from 'zod';

/**
 * @aiPurpose minimal projection of `ArrivalCity` carried by the
 *   `RequestDestination` read shape (#700). The denormalised fields
 *   (`name`, `shortName`, `isDefault`, `active`) let the client render
 *   the picker chips and the per-city carrier sections (#702) without
 *   a second round-trip.
 * @aiInvariant fields mirror the strict-row contract of
 *   `ArrivalCitySchema` (`packages/shared/src/schemas/arrivalCities.ts`).
 *   Strict object so a stale client cannot silently accept new fields.
 */
export const RequestDestinationArrivalCityProjectionSchema = z
  .object({
    id: z.string().uuid(),
    name: z.string().min(1),
    shortName: z.string().min(1).max(3),
    isDefault: z.boolean(),
    active: z.boolean(),
  })
  .strict();
export type RequestDestinationArrivalCityProjection = z.infer<
  typeof RequestDestinationArrivalCityProjectionSchema
>;

/**
 * @aiPurpose write-side row of the request destinations multiselect
 *   (#700). Only the FK + sort order are client-controlled; the
 *   denormalised `arrivalCity` fields on the read shape are server-
 *   projected.
 * @aiInvariant `arrivalCityId` MUST reference an `ArrivalCity` row with
 *   `active = true` at write time. Inactive references are rejected at
 *   the service layer with a typed domain error and translated to 400.
 */
export const RequestDestinationInputSchema = z
  .object({
    arrivalCityId: z.string().uuid(),
    sortOrder: z.number().int().min(0).default(0),
  })
  .strict();
export type RequestDestinationInput = z.infer<typeof RequestDestinationInputSchema>;

/**
 * @aiPurpose read-side row of the request destinations multiselect.
 *   Used by `RequestSchema.destinations` and by the per-city carriers
 *   tab grouping (#702).
 */
export const RequestDestinationReadSchema = z
  .object({
    arrivalCityId: z.string().uuid(),
    sortOrder: z.number().int().min(0),
    arrivalCity: RequestDestinationArrivalCityProjectionSchema,
  })
  .strict();
export type RequestDestinationRead = z.infer<typeof RequestDestinationReadSchema>;
